import { logger } from '@accedo/logger';
import { ServiceManager } from '##/services/elevate/manager';
import { getNZOSBaseUrl } from './config';

// Logger prefix
const LP = '[NZOSServiceAdapter]';

/**
 * Make an API call to either mock or real NZOS API.
 */
export async function apiCall<T>(
  endpoint: string,
  options: {
    method?: string;
    params?: Record<string, string | number | boolean>;
    body?: unknown;
    headers?: Record<string, string>;
  } = {},
): Promise<T> {
  const { method = 'GET', params, body, headers = {} } = options;

  // Build URL
  const baseUrl = getNZOSBaseUrl();
  let url = `${baseUrl}${endpoint}`;

  // Add query parameters
  if (params) {
    const validParams = Object.keys(params).reduce(
      (acc, key) => {
        const value = params[key];
        if (value !== undefined && value !== null) {
          acc[key] = String(value);
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    const searchParams = new URLSearchParams(validParams);

    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Prepare request options
  const requestOptions: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...headers,
    },
  };

  // Add JWT token if user is authenticated (for endpoints that require auth)
  const user = ServiceManager.getIDPService()?.getUser();
  if (user?.authToken?.token) {
    (requestOptions.headers as Record<string, string>).Authorization = `Bearer ${user.authToken.token}`;
  }

  if (body && (method === 'POST' || method === 'PUT')) {
    requestOptions.body = JSON.stringify(body);
  }

  logger.debug(`${LP} Making API call: ${method} ${url}`);

  try {
    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`API Error: ${response.status} ${response.statusText}`);
      logger.error(`${LP} API call failed:`, error, errorText);
      throw error;
    }

    // Handle empty responses (e.g., 204 No Content)
    const text = await response.text();
    logger.debug(`${LP} API call successful`);
    return text ? JSON.parse(text) : ({} as T);
  } catch (error) {
    logger.error(`${LP} API call failed:`, error);
    throw error;
  }
}
